import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crop, Info, Trash2, Wand2 } from 'lucide-react';
import type { AspectRatio } from '../../../shared/platforms';
import { PLATFORM_LIST, PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';
import { formatBytes } from '../../../shared/utils';
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Field,
  PlatformIcon,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '../ui';
import { api, ApiError } from '../../lib/api';
import { qk } from '../../lib/query';
import { formatDay } from '../../lib/format';
import { useWorkspaceStore } from '../../stores/workspace';
import { RenditionCropTool } from './RenditionCropTool';

export interface MediaDetailDialogProps {
  /** null closes the dialog; an asset id opens (and fetches) it. */
  assetId: string | null;
  onClose: () => void;
  /** Called after a successful delete so the caller can drop it from a grid/route. */
  onDeleted?: (assetId: string) => void;
}

/**
 * Asset detail: preview, alt text (manual + AI-assisted), per-platform
 * renditions via the client-side crop tool, and delete (with 409-in-use
 * handling — media referenced by a post cannot be removed out from under it).
 */
export function MediaDetailDialog({ assetId, onClose, onDeleted }: MediaDetailDialogProps) {
  const open = Boolean(assetId);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Keeps the last-loaded asset visible while the dialog animates closed.
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (assetId) setActiveId(assetId);
  }, [assetId]);

  const query = useQuery({
    queryKey: qk.media(workspaceId, activeId ?? ''),
    queryFn: () => api.media.get(activeId as string),
    enabled: Boolean(activeId) && open,
  });

  const asset = query.data?.asset ?? null;
  const renditions = query.data?.renditions ?? [];

  const [altText, setAltText] = useState('');
  const [altDirty, setAltDirty] = useState(false);
  useEffect(() => {
    setAltText(asset?.altText ?? '');
    setAltDirty(false);
  }, [asset?.id, asset?.altText]);

  const [platform, setPlatform] = useState<PlatformId>('facebook');
  const [aspectId, setAspectId] = useState<string>(
    PLATFORM_SPECS.facebook.aspectRatios.find((a) => a.recommended)?.id ??
      PLATFORM_SPECS.facebook.aspectRatios[0]?.id ??
      '',
  );
  const [cropOpen, setCropOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const invalidateAsset = () => {
    if (activeId) void queryClient.invalidateQueries({ queryKey: qk.media(workspaceId, activeId) });
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'media'] });
  };

  const saveAltText = useMutation({
    mutationFn: () => {
      if (!activeId) throw new Error('No asset selected');
      return api.media.update(activeId, { altText: altText.trim() || null });
    },
    onSuccess: () => {
      setAltDirty(false);
      invalidateAsset();
      toast({ title: 'Alt text saved', tone: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Could not save alt text',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const generateAltText = useMutation({
    mutationFn: () => {
      if (!activeId) throw new Error('No asset selected');
      return api.ai.altText({ mediaId: activeId });
    },
    onSuccess: (response) => {
      setAltText(response.altText);
      setAltDirty(true);
      toast({
        title: 'Draft alt text generated',
        description: response.provider === 'mock' ? 'Demo AI — review before saving.' : 'Review, then save.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not generate alt text',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const createRendition = useMutation({
    mutationFn: (input: { aspect: AspectRatio; dataUrl: string; width: number; height: number }) => {
      if (!activeId) throw new Error('No asset selected');
      return api.media.createRendition(activeId, {
        platform,
        label: input.aspect.id,
        dataUrl: input.dataUrl,
        width: input.width,
        height: input.height,
      });
    },
    onSuccess: () => {
      invalidateAsset();
      setCropOpen(false);
      toast({ title: 'Rendition saved', tone: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Could not save the crop',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const deleteAsset = useMutation({
    mutationFn: () => {
      if (!activeId) throw new Error('No asset selected');
      return api.media.remove(activeId);
    },
  });

  const platformSpec = PLATFORM_SPECS[platform];
  const aspect = platformSpec.aspectRatios.find((a) => a.id === aspectId) ?? platformSpec.aspectRatios[0];

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title={asset?.filename ?? 'Media asset'}
        size="xl"
        bodyClassName="max-h-[75vh] overflow-y-auto"
      >
        {query.isLoading || !asset ? (
          <div className="grid gap-5 md:grid-cols-[280px_1fr]">
            <Skeleton className="aspect-square w-full" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[280px_1fr]">
            <div className="space-y-3">
              <div className="overflow-hidden rounded-lg border border-border bg-sunken">
                {asset.kind === 'image' ? (
                  <img src={asset.url} alt={asset.altText ?? asset.filename} className="w-full object-contain" />
                ) : (
                  <video src={asset.url} controls className="w-full" />
                )}
              </div>
              <dl className="space-y-1.5 text-xs text-muted">
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-subtle">Kind</dt>
                  <dd className="capitalize text-fg">{asset.kind}</dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-subtle">Size</dt>
                  <dd className="text-fg">{formatBytes(asset.size)}</dd>
                </div>
                {asset.width && asset.height && (
                  <div className="flex items-center justify-between gap-2">
                    <dt className="text-subtle">Dimensions</dt>
                    <dd className="pap-tnum text-fg">
                      {asset.width}×{asset.height}
                    </dd>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-subtle">Source</dt>
                  <dd className="text-fg">
                    {asset.source === 'ai_generated' ? (
                      <Badge tone="accent" size="sm" dot>
                        AI generated
                      </Badge>
                    ) : (
                      'Uploaded'
                    )}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-subtle">Added</dt>
                  <dd className="text-fg">{formatDay(asset.createdAt)}</dd>
                </div>
              </dl>
              {asset.aiPrompt && (
                <p className="rounded-md border border-dashed border-border bg-elevated/40 p-2 text-[11px] leading-relaxed text-subtle">
                  <span className="font-medium text-muted">Prompt: </span>
                  {asset.aiPrompt}
                </p>
              )}
              <Button
                variant="danger"
                size="sm"
                fullWidth
                leftIcon={<Trash2 className="size-3.5" />}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete asset
              </Button>
            </div>

            <div className="space-y-6">
              <Field
                label="Alt text"
                htmlFor="media-alt-text"
                hint="Describes the image for screen readers and is used as the default caption alt on platforms that support it."
                labelAction={
                  <Button
                    variant="ghost"
                    size="xs"
                    leftIcon={<Wand2 className="size-3.5" />}
                    loading={generateAltText.isPending}
                    disabled={asset.kind !== 'image'}
                    onClick={() => generateAltText.mutate()}
                  >
                    Generate with AI
                  </Button>
                }
              >
                <Textarea
                  id="media-alt-text"
                  rows={3}
                  maxRows={6}
                  placeholder="A close-up of a hand pouring coffee into a paper filter"
                  value={altText}
                  onChange={(event) => {
                    setAltText(event.target.value);
                    setAltDirty(true);
                  }}
                />
              </Field>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={!altDirty}
                  loading={saveAltText.isPending}
                  onClick={() => saveAltText.mutate()}
                >
                  Save alt text
                </Button>
                {altDirty && (
                  <span className="text-xs text-subtle">Unsaved changes</span>
                )}
              </div>

              <div className="space-y-3 border-t border-border pt-5">
                <div className="flex items-center gap-2">
                  <Crop className="size-4 text-subtle" aria-hidden />
                  <h3 className="text-sm font-semibold text-fg">Per-platform renditions</h3>
                </div>

                {asset.kind !== 'image' ? (
                  <Alert tone="info" icon={<Info className="size-4" />}>
                    Crop-based renditions are available for images. Video renditions land with the
                    video pipeline.
                  </Alert>
                ) : (
                  <>
                    <div className="flex flex-wrap items-end gap-2">
                      <Field label="Platform" className="w-40">
                        <Select
                          selectSize="sm"
                          value={platform}
                          onChange={(event) => {
                            const next = event.target.value as PlatformId;
                            setPlatform(next);
                            const spec = PLATFORM_SPECS[next];
                            setAspectId(spec.aspectRatios.find((a) => a.recommended)?.id ?? spec.aspectRatios[0]?.id ?? '');
                          }}
                          options={PLATFORM_LIST.map((spec) => ({
                            value: spec.id,
                            label: spec.comingSoon ? `${spec.shortName} (soon)` : spec.shortName,
                          }))}
                        />
                      </Field>
                      <Field label="Aspect ratio" className="w-48">
                        <Select
                          selectSize="sm"
                          value={aspectId}
                          onChange={(event) => setAspectId(event.target.value)}
                          options={platformSpec.aspectRatios.map((a) => ({ value: a.id, label: a.label }))}
                        />
                      </Field>
                      <Button
                        size="sm"
                        leftIcon={<Crop className="size-3.5" />}
                        disabled={!aspect}
                        onClick={() => setCropOpen(true)}
                      >
                        Crop &amp; add
                      </Button>
                    </div>

                    {renditions.length === 0 ? (
                      <p className="text-xs text-subtle">
                        No renditions yet — crop this image to the aspect ratios each platform expects.
                      </p>
                    ) : (
                      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {renditions.map((rendition) => (
                          <li
                            key={rendition.id}
                            className="overflow-hidden rounded-lg border border-border"
                          >
                            <div className="aspect-square bg-sunken">
                              <img
                                src={rendition.url}
                                alt=""
                                className="size-full object-cover"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 px-2 py-1.5">
                              <PlatformIcon platform={rendition.platform} size="xs" variant="soft" />
                              <span className="min-w-0 flex-1 truncate text-[11px] text-muted">
                                {rendition.label}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {asset && aspect && (
        <RenditionCropTool
          open={cropOpen}
          onOpenChange={setCropOpen}
          imageUrl={asset.url}
          aspect={aspect}
          platformLabel={platformSpec.shortName}
          busy={createRendition.isPending}
          onConfirm={(dataUrl, width, height) => createRendition.mutate({ aspect, dataUrl, width, height })}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title="Delete this asset?"
        destructive
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await deleteAsset.mutateAsync();
            void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'media'] });
            toast({ title: 'Asset deleted', tone: 'success' });
            if (activeId) onDeleted?.(activeId);
            onClose();
          } catch (error) {
            const inUse = error instanceof ApiError && error.status === 409;
            toast({
              title: inUse ? 'This asset is still in use' : 'Could not delete this asset',
              description:
                error instanceof ApiError
                  ? error.message
                  : 'It is attached to one or more posts — remove it there first.',
              tone: 'error',
              duration: inUse ? 9000 : undefined,
            });
            throw error;
          }
        }}
      >
        <p className="text-sm text-muted">
          This permanently removes the file and its renditions. Posts that already reference it may
          be affected.
        </p>
      </ConfirmDialog>
    </>
  );
}
